import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import DateTimePicker from 'components/DateTimePicker';
import { DateSelect, Input, Textarea } from 'components/FormFields';
import AttachmentUpload from 'components/FormFields/AttachmentUpload';

// Container
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Card header with icon
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const CardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const CardTitleGroup = styled.div``;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin: 4px 0 0 0;
`;

// Two-column card grid for the entire tab
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// Stacked fields layout
const StackedFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Toggle for showing/hiding the end date field (single-day vs multi-day)
const EventLengthToggle = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: #fa4616;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: none;
    text-decoration: underline;
  }
`;

// Field group with label
const FieldGroup = styled.div``;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.name === 'dark' ? '#E5E7EB' : '#4B5563')};
  margin-bottom: 8px;
`;

// Daily schedule styles - Mini Card with Date Pill design
const DayCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.06)'};
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Date display box - matches header design system
const DateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fa4616;
  color: white;
  border-radius: 8px;
  width: 65px;
  height: 65px;
  gap: 2px;
  flex-shrink: 0;
`;

const DayBadge = styled.div`
  background: white;
  color: #1e293b;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  letter-spacing: 0.5px;
`;

const DateText = styled.div`
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
  color: white;
`;

const TimeInputsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  flex: 1;
`;

const TimeInputGroup = styled.div``;

const TimeLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#64748b' : '#64748b')};
  margin-bottom: 6px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#64748b' : '#94a3b8')};
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  font-size: 16px;
`;

// Icons
const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const TicketIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2" />
    <path d="M13 17v2" />
    <path d="M13 11v2" />
  </svg>
);

const ClockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const FileTextIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

// Field wrapper for consistent spacing
const FieldWrapper = styled.div`
  margin-bottom: ${(props) => (props.noMargin ? '0' : '20px')};

  &:last-child {
    margin-bottom: 0;
  }
`;

// Image upload container
const ImageUploadContainer = styled.div`
  margin-top: 16px;
`;

const ImageLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.name === 'dark' ? '#E5E7EB' : '#4B5563')};
  margin-bottom: 8px;
`;

// Helper to combine date and time into a Date object
// Defaults to 11:59 PM if no time is provided
const combineDateAndTime = (dateStr, timeStr, defaultTime = '23:59') => {
  if (!dateStr) return null;

  // Parse the date - try MM-DD-YYYY first (our standard), then other formats
  const date = moment(dateStr, ['MM-DD-YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY']);
  if (!date.isValid()) return null;

  // Use provided time or default to 11:59 PM
  const timeToUse = timeStr || defaultTime;
  const time = moment(timeToUse, ['HH:mm:ss', 'HH:mm', 'h:mm A', 'h:mm a']);
  if (time.isValid()) {
    date.hours(time.hours());
    date.minutes(time.minutes());
  }

  return date.toDate();
};

// Helper to split a Date object into date and time strings
const splitDateTime = (dateObj) => {
  if (!dateObj) return { date: '', time: '' };

  const m = moment(dateObj);
  if (!m.isValid()) return { date: '', time: '' };

  return {
    date: m.format('MM-DD-YYYY'),
    time: m.format('HH:mm'),
  };
};

// Helper to check whether two date strings represent the same calendar day
const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  const d1 = moment(date1, ['MM-DD-YYYY', 'YYYY-MM-DD']);
  const d2 = moment(date2, ['MM-DD-YYYY', 'YYYY-MM-DD']);
  if (!d1.isValid() || !d2.isValid()) return false;
  return d1.isSame(d2, 'day');
};

// Helper to generate days from event date range
const generateDaysFromDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];

  const start = moment(startDate, ['MM-DD-YYYY', 'YYYY-MM-DD']);
  const end = moment(endDate, ['MM-DD-YYYY', 'YYYY-MM-DD']);

  if (!start.isValid() || !end.isValid()) return [];
  if (end.isBefore(start)) return [];

  const days = [];
  const current = start.clone();

  while (current.isSameOrBefore(end, 'day')) {
    days.push({
      id: `generated-${current.format('YYYY-MM-DD')}`,
      event_date: current.format('MM-DD-YYYY'),
      gate_time: '',
      start_time: '',
    });
    current.add(1, 'day');
  }

  return days;
};

const DatesTimesTab = ({
  dateSettings = {},
  adminTimes = [],
  onDateSettingsChange,
  onAdminTimesChange,
  scheduleSettings = {},
  onScheduleSettingsChange,
  loading,
}) => {
  const handleDateChange = (field, value) => {
    onDateSettingsChange({
      ...dateSettings,
      [field]: value,
    });
  };

  // Track whether the End Date field is visible. For single-day events
  // (start_date == end_date, or end_date empty) we hide the field but keep
  // end_date in form state so the backend still receives it.
  const [showEndDate, setShowEndDate] = useState(false);
  const hasInitializedEndDate = useRef(false);

  useEffect(() => {
    if (hasInitializedEndDate.current) return;
    if (!dateSettings.start_date) return;

    hasInitializedEndDate.current = true;
    const singleDay =
      !dateSettings.end_date ||
      isSameDay(dateSettings.start_date, dateSettings.end_date);
    setShowEndDate(!singleDay);

    // Ensure end_date is populated for single-day events with missing end_date
    if (singleDay && dateSettings.end_date !== dateSettings.start_date) {
      onDateSettingsChange({
        ...dateSettings,
        end_date: dateSettings.start_date,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSettings.start_date, dateSettings.end_date]);

  const handleStartDateChange = (val) => {
    if (showEndDate) {
      handleDateChange('start_date', val);
    } else {
      // Keep end_date synced with start_date while hidden (single-day)
      onDateSettingsChange({
        ...dateSettings,
        start_date: val,
        end_date: val,
      });
    }
  };

  const handleMakeMultiDay = () => {
    setShowEndDate(true);
  };

  // Handle combined date-time change for sales window
  const handleSaleStartChange = (dateValue) => {
    if (!dateValue) {
      onDateSettingsChange({
        ...dateSettings,
        sale_start: '',
        on_sale_time: '',
      });
      return;
    }

    const { date, time } = splitDateTime(dateValue);
    onDateSettingsChange({
      ...dateSettings,
      sale_start: date,
      on_sale_time: time,
    });
  };

  const handleSaleEndChange = (dateValue) => {
    if (!dateValue) {
      onDateSettingsChange({
        ...dateSettings,
        sale_end: '',
        off_sale_time: '',
      });
      return;
    }

    const { date, time } = splitDateTime(dateValue);
    onDateSettingsChange({
      ...dateSettings,
      sale_end: date,
      off_sale_time: time,
    });
  };

  // Compute display days - use adminTimes if available, otherwise generate from dates
  const generatedDays = generateDaysFromDateRange(
    dateSettings.start_date,
    dateSettings.end_date
  );
  const displayDays = adminTimes.length > 0 ? adminTimes : generatedDays;

  const handleTimeChange = (dayIndex, field, value) => {
    // If adminTimes is empty, we need to initialize it from generated days
    let updatedTimes;
    if (adminTimes.length === 0 && generatedDays.length > 0) {
      // Initialize adminTimes from generated days
      updatedTimes = generatedDays.map((day, _idx) => ({
        ...day,
        // Clear the generated ID prefix for real entries
        id: day.id.startsWith('generated-') ? undefined : day.id,
      }));
    } else {
      updatedTimes = [...adminTimes];
    }

    updatedTimes[dayIndex] = {
      ...updatedTimes[dayIndex],
      [field]: value,
    };
    onAdminTimesChange(updatedTimes);
  };

  // Handle schedule field changes
  const handleScheduleChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    if (onScheduleSettingsChange) {
      onScheduleSettingsChange({
        ...(scheduleSettings || {}),
        [field]: value,
      });
    }
  };

  // Handle schedule image upload
  const handleScheduleImageUpload = (attachments) => {
    if (!onScheduleSettingsChange) return;

    if (attachments && attachments.length > 0) {
      const attachment = attachments[0];
      onScheduleSettingsChange({
        ...(scheduleSettings || {}),
        schedule_detail_image: attachment.url || attachment.name,
        schedule_detail_image_id: attachment.id,
      });
    } else {
      onScheduleSettingsChange({
        ...(scheduleSettings || {}),
        schedule_detail_image: null,
        schedule_detail_image_id: null,
      });
    }
  };

  // Convert single image URL to attachments array format for AttachmentUpload
  const scheduleImageAttachments = scheduleSettings?.schedule_detail_image
    ? (() => {
        const url = scheduleSettings.schedule_detail_image;
        const urlPath = url.split('?')[0]; // Remove query params
        const isPDF = urlPath.toLowerCase().endsWith('.pdf');
        const fileName =
          decodeURIComponent(urlPath.split('/').pop()) ||
          'schedule_detail_image';
        return [
          {
            id: scheduleSettings.schedule_detail_image_id || 'existing',
            name: fileName,
            size: 0,
            sizeFormatted: '',
            url: url,
            type: isPDF ? 'application/pdf' : 'image/jpeg',
            isImage: !isPDF,
            isPDF: isPDF,
          },
        ];
      })()
    : [];

  // Combine sale_start + on_sale_time for the picker
  const saleStartDateTime = combineDateAndTime(
    dateSettings.sale_start,
    dateSettings.on_sale_time
  );

  // Combine sale_end + off_sale_time for the picker
  const saleEndDateTime = combineDateAndTime(
    dateSettings.sale_end,
    dateSettings.off_sale_time
  );

  if (loading) {
    return (
      <Card>
        <LoadingContainer>Loading dates and times...</LoadingContainer>
      </Card>
    );
  }

  return (
    <Container>
      <CardGrid>
        {/* Card 1: Event Dates */}
        <Card>
          <CardHeader>
            <CardIcon>
              <CalendarIcon />
            </CardIcon>
            <CardTitleGroup>
              <CardTitle>Event Dates</CardTitle>
              <CardSubtitle>When does your event take place?</CardSubtitle>
            </CardTitleGroup>
          </CardHeader>

          <StackedFields>
            <DateSelect
              label="Start Date"
              value={dateSettings.start_date || ''}
              onChange={handleStartDateChange}
              showModeToggle
            />
            {showEndDate && (
              <DateSelect
                label="End Date"
                value={dateSettings.end_date || ''}
                onChange={(val) => handleDateChange('end_date', val)}
                showModeToggle
              />
            )}
            {!showEndDate && (
              <EventLengthToggle type="button" onClick={handleMakeMultiDay}>
                + Make multi-day event
              </EventLengthToggle>
            )}
          </StackedFields>
        </Card>

        {/* Card 2: Sales Window */}
        <Card>
          <CardHeader>
            <CardIcon>
              <TicketIcon />
            </CardIcon>
            <CardTitleGroup>
              <CardTitle>Sales Window</CardTitle>
              <CardSubtitle>When can customers purchase tickets?</CardSubtitle>
            </CardTitleGroup>
          </CardHeader>

          <StackedFields>
            <FieldGroup>
              <FieldLabel>Tickets Go On Sale</FieldLabel>
              <DateTimePicker
                value={saleStartDateTime}
                onChange={handleSaleStartChange}
                showTime={true}
                confirmText="Set"
                cancelText="Clear"
                defaultTime="11:59 PM"
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Tickets Go Off Sale</FieldLabel>
              <DateTimePicker
                value={saleEndDateTime}
                onChange={handleSaleEndChange}
                showTime={true}
                confirmText="Set"
                cancelText="Clear"
                defaultTime="11:59 PM"
              />
            </FieldGroup>
          </StackedFields>
        </Card>

        {/* Card 3: Daily Schedule */}
        <Card>
          <CardHeader>
            <CardIcon>
              <ClockIcon />
            </CardIcon>
            <CardTitleGroup>
              <CardTitle>Daily Schedule</CardTitle>
              <CardSubtitle>
                Shown to fans so they know when to arrive
              </CardSubtitle>
            </CardTitleGroup>
          </CardHeader>

          {displayDays.length === 0 ? (
            <EmptyState>
              Set your event start and end dates above to configure daily
              schedules.
            </EmptyState>
          ) : (
            displayDays.map((day, index) => (
              <DayCard key={day.id || index}>
                <DateBox>
                  <DayBadge>
                    {day.event_date
                      ? moment(day.event_date, 'MM-DD-YYYY').format('ddd')
                      : `Day ${index + 1}`}
                  </DayBadge>
                  <DateText>
                    {day.event_date
                      ? moment(day.event_date, 'MM-DD-YYYY').format('MMM D')
                      : '—'}
                  </DateText>
                </DateBox>

                <TimeInputsGrid>
                  <TimeInputGroup>
                    <TimeLabel>Gates Open</TimeLabel>
                    <DateTimePicker
                      value={day.gate_time || ''}
                      onChange={(time) =>
                        handleTimeChange(index, 'gate_time', time)
                      }
                      timeOnly
                    />
                  </TimeInputGroup>
                  <TimeInputGroup>
                    <TimeLabel>Event Starts</TimeLabel>
                    <DateTimePicker
                      value={day.start_time || ''}
                      onChange={(time) =>
                        handleTimeChange(index, 'start_time', time)
                      }
                      timeOnly
                    />
                  </TimeInputGroup>
                </TimeInputsGrid>
              </DayCard>
            ))
          )}
        </Card>

        {/* Card 4: Schedule Details */}
        <Card>
          <CardHeader>
            <CardIcon>
              <FileTextIcon />
            </CardIcon>
            <CardTitleGroup>
              <CardTitle>Schedule Details</CardTitle>
              <CardSubtitle>
                Additional schedule information shown to fans
              </CardSubtitle>
            </CardTitleGroup>
          </CardHeader>

          <FieldWrapper>
            <Textarea
              label="Schedule Details"
              value={scheduleSettings?.schedule_details || ''}
              onChange={handleScheduleChange('schedule_details')}
              placeholder="Enter schedule details..."
              rows={4}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Input
              label="Schedule Detail URL"
              type="url"
              value={scheduleSettings?.schedule_detail_url || ''}
              onChange={handleScheduleChange('schedule_detail_url')}
              placeholder="https://..."
            />
          </FieldWrapper>

          <ImageUploadContainer>
            <ImageLabel>Schedule Detail Image</ImageLabel>
            <AttachmentUpload
              attachments={scheduleImageAttachments}
              onAttachmentsChange={handleScheduleImageUpload}
              maxFiles={1}
              defaultExpanded
            />
          </ImageUploadContainer>
        </Card>
      </CardGrid>
    </Container>
  );
};

DatesTimesTab.propTypes = {
  dateSettings: PropTypes.shape({
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    sale_start: PropTypes.string,
    sale_end: PropTypes.string,
    on_sale_time: PropTypes.string,
    off_sale_time: PropTypes.string,
  }),
  adminTimes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      event_date: PropTypes.string,
      gate_time: PropTypes.string,
      start_time: PropTypes.string,
    })
  ),
  onDateSettingsChange: PropTypes.func.isRequired,
  onAdminTimesChange: PropTypes.func.isRequired,
  scheduleSettings: PropTypes.shape({
    schedule_details: PropTypes.string,
    schedule_detail_url: PropTypes.string,
    schedule_detail_image: PropTypes.string,
    schedule_detail_image_id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  onScheduleSettingsChange: PropTypes.func,
  loading: PropTypes.bool,
};

export default DatesTimesTab;
